
import { apiConfig } from '@/lib/env';
import { requestDeduplication } from '@/services/requestDeduplication';

class ApiClient {
  private baseUrl: string;

  constructor() {
    // Use centralized configuration instead of direct env access
    this.baseUrl = apiConfig.baseUrl;
    
    // Debug logging on initialization (minimal)
    if (import.meta.env.DEV) console.log('🔧 ApiClient initialized:', this.baseUrl);
    
    // Validate that we're using the correct URL
    if (this.baseUrl.includes('ai.lnk2.im') || this.baseUrl.includes('9001')) {
      console.error('❌ CRITICAL: ApiClient is using old API URL!', this.baseUrl);
      throw new Error(`Invalid API URL detected: ${this.baseUrl}. Expected: https://api.at-work.biz/api`);
    }
    
    // Ensure HTTPS
    if (this.baseUrl.startsWith('http://')) {
      console.warn('⚠️ Converting HTTP to HTTPS for API URL');
      this.baseUrl = this.baseUrl.replace('http://', 'https://');
    }
    
    if (import.meta.env.DEV) console.log('✅ ApiClient ready:', this.baseUrl);
  }

  async request<T = any>(endpoint: string, options: RequestInit & { context?: { category?: string; page?: string; key?: string }; timeoutMs?: number } = {}): Promise<T> {
    let url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`;
    
    // Additional validation before making request
    if (url.includes('ai.lnk2.im') || url.includes('9001')) {
      console.error('❌ BLOCKED: Attempted to call old API URL!', url);
      throw new Error(`Blocked call to old API URL: ${url}`);
    }
    
    const ctx = (options as any).context as { category?: string; page?: string; key?: string } | undefined;
    
    // Get auth token from localStorage
    const token = localStorage.getItem('token');
    
    const headers: HeadersInit = {};
    
    // Only set Content-Type if body is not FormData
    if (!(options.body instanceof FormData)) {
      (headers as any)['Content-Type'] = 'application/json';
    }
    
    // Add existing headers
    Object.assign(headers, options.headers);
    
    // Add context headers for categorization/dedup analytics (safe no-op for backend)
    if (ctx?.category) (headers as any)['X-Request-Category'] = ctx.category;
    if (ctx?.page) (headers as any)['X-Request-Page'] = ctx.page;
    if (ctx?.key) (headers as any)['X-Request-Key'] = ctx.key;
    
    // RBAC endpoints workaround: Send token in request body for POST/PUT
    const isRBACEndpoint = endpoint.includes('/rbac/');
    const isModifyingRequest = options.method === 'POST' || options.method === 'PUT';
    
    if (token) {
      // Always add Authorization header
      (headers as any)['Authorization'] = `Bearer ${token}`;
      // auth token added
      
      // WORKAROUND: For RBAC modifying endpoints, also include token in body
      if (isRBACEndpoint && isModifyingRequest && options.body) {
        try {
          const bodyData = JSON.parse(options.body as string);
          bodyData._auth_token = token; // Backend can use this if header fails
          options.body = JSON.stringify(bodyData);
          // RBAC workaround: token added to body
        } catch (e) {
          console.warn('⚠️ Could not add token to request body:', e);
        }
      }
    }
    
    // Exclude custom context from fetch options
    const { context: _context, timeoutMs, ...restOptions } = options as any;
    const config: RequestInit = {
      headers,
      redirect: 'follow', // Explicitly follow redirects
      ...restOptions,
    };
    
    try {
      // Use request deduplication service
      const data = await requestDeduplication.executeRequest<T>(
        url,
        config,
        (fetchUrl, fetchOptions) => fetch(fetchUrl, fetchOptions),
        timeoutMs
      );
      
      // Response received successfully
      return data;
    } catch (error: any) {
      console.error(`❌ ApiClient Error for ${url}:`, error);
      
      // Log authentication errors specifically
      if (error?.status === 401) {
        console.error('🔒 Authentication failed - 401 Unauthorized:', error.detail || error.message);
      }
      
      throw error;
    }
  }

  /**
   * Upload file using FormData with proper auth headers
   */
  async uploadFile<T = any>(endpoint: string, formData: FormData, context?: { category?: string; page?: string; key?: string }, timeoutMs?: number): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: formData,
      context,
      // Uploads/large server-side jobs need much more headroom than the 15s default
      timeoutMs: timeoutMs ?? 300000,
    } as any);
  }

  /**
   * Download a binary/text file (CSV, PDF, …) as a Blob.
   * Deliberately bypasses requestDeduplication — downloads must never be cached
   * or coalesced — while reusing the same base URL + bearer token logic.
   */
  async downloadBlob(
    endpoint: string,
    timeoutMs = 600000,
    accept = 'text/csv'
  ): Promise<{ blob: Blob; filename: string }> {
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`;
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = { Accept: accept };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(url, { headers, signal: controller.signal });
      if (!res.ok) {
        let detail = res.statusText;
        try {
          const body = await res.json();
          detail = body?.detail ?? detail;
        } catch {
          /* not JSON */
        }
        const err: any = new Error(detail);
        err.status = res.status;
        throw err;
      }
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') ?? '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      return { blob, filename: match?.[1] ?? 'download.csv' };
    } finally {
      clearTimeout(timer);
    }
  }

  async get<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }


  async post<T = any>(endpoint: string, data?: any, timeoutMs?: number): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      ...(timeoutMs ? { timeoutMs } : {}),
    } as any);
  }


  async put<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const apiClient = new ApiClient();
